import "../shared/IntakeForm.scss";

import Button from "components/Button/Button";
import { ControlledInput } from "components/Card/ControlledInput";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextInput } from "components/inputs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import type { LoginIntakeRequest } from "services/intake-api";
import { useLoginIntakeMutation } from "services/intake-api";
import { useIntakeToken, useToken } from "utils/context";

import IntakeHeader from "../components/HeaderIntake";
import {
  useOrganizationSlug,
  formatOrganizationSlug,
} from "../hooks/useOrganizationSlug";
import { logToSentry, getSessionId } from "utils/sentry";

const LoginIntake = () => {
  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginIntakeRequest>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const navigate = useNavigate();
  const { intakeToken, setIntakeToken, setIntakeUser } = useIntakeToken();
  const { accessToken } = useToken();
  const organizationSlug = useOrganizationSlug();
  const [loginIntake, { isLoading }] = useLoginIntakeMutation();
  const [error, setError] = useState<string | null>(null);
  const uiSessionId = getSessionId();

  useEffect(() => {
    logToSentry(`Intake login page`, "info", { uiSessionId });
  }, [uiSessionId]);

  useEffect(() => {
    if (intakeToken && organizationSlug) {
      navigate(`/intake/${organizationSlug}/dashboard?page=1`);
    }
  }, [navigate, intakeToken, organizationSlug]);

  // for auth pages, just check if tokens exist and redirect
  // the dashboard will handle token validation
  if ((accessToken || intakeToken) && organizationSlug) {
    return <Navigate to={`/intake/${organizationSlug}/dashboard`} replace />;
  }

  const handleFieldChange = async () => Promise.resolve();

  const onSubmit = async (data: LoginIntakeRequest) => {
    const uiSessionId = getSessionId();

    // validate organization slug before attempting login
    if (!organizationSlug) {
      setError("Organization not found. Please check the URL.");
      logToSentry(`Intake login failed - no organization slug`, "error", {
        uiSessionId,
        loginEmail: data.email,
      });
      return;
    }

    try {
      const response = await loginIntake(data).unwrap();
      setIntakeToken(response.access_token);
      setIntakeUser({
        email: response.email,
        username: response.username,
        libertyReferralFieldsEnabled: response.liberty_referral_fields_enabled,
      });
      logToSentry(`Intake login success`, "info", {
        uiSessionId,
        loginEmail: data.email,
      });
      if (organizationSlug) {
        navigate(`/intake/${organizationSlug}/dashboard`);
      }
    } catch (err: unknown) {
      // handle errors from backend
      let errorMessage = "Login failed. Please try again.";
      if (err && typeof err === "object" && "data" in err) {
        const errorData = (err as { data: { error?: string } }).data;
        errorMessage = errorData.error || errorMessage;
      }
      setError(errorMessage);
      KaboodleToaster.error(errorMessage);
      logToSentry(`Intake login failed`, "error", {
        uiSessionId,
        loginEmail: data.email,
        organizationSlug,
      });
      console.error(err);
    }
  };

  return (
    <>
      <IntakeHeader />
      <div className="intake-form-page bkg-alt flex js-c">
        <div className="intake-card">
          <h2 className="page-title">
            <span>Login to</span>
            {organizationSlug === "allan-koba" ? (
              <img
                src="/static/images/logo-black.png"
                alt={formatOrganizationSlug(organizationSlug) || "Organization"}
                className="logo-image"
                height={30}
              />
            ) : (
              <span className="organization-name">
                {formatOrganizationSlug(organizationSlug) || "Organization"}
              </span>
            )}
          </h2>
          <p className="page-subtitle">
            Please enter your credentials to access your account.
          </p>

          <form onSubmit={handleFormSubmit(onSubmit)}>
            <ControlledInput
              rules={{
                required: "Email is required",
              }}
              control={control}
              name="email"
              label="Email Address"
              errors={errors}
              maxLength={50}
              onSave={handleFieldChange}
              componentType={TextInput}
            />

            <ControlledInput
              rules={{
                required: "Email is required",
              }}
              control={control}
              name="password"
              label="Password"
              errors={errors}
              maxLength={40}
              onSave={handleFieldChange}
              componentType={TextInput}
              type="password"
            />

            {error && (
              <div className="intake-form-page--Error bkg-alt flex js-c">
                {error}
              </div>
            )}

            <Button
              onClick={handleFormSubmit(onSubmit)}
              type="submit"
              className="button allan-koba"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? "Logging in..." : "Log In"}
            </Button>

            <div
              className="prompt-message"
              style={{ textAlign: "center", marginBottom: "1rem" }}
            >
              <Link
                to={`/intake/${organizationSlug || "allan-koba"}/auth/forgot-password`}
                className="prompt-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="prompt-message">
            Need an account?{" "}
            <Link
              to={`/intake/${organizationSlug || "allan-koba"}/auth/register`}
              className="prompt-link"
            >
              Register here
            </Link>
          </div>
        </div>
      </div>
      <KaboodleToaster.ToastContainer />
    </>
  );
};

export default LoginIntake;
